import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Palette, Type, MousePointer } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface StorefrontContent {
  hero: {
    title: string;
    subtitle: string;
    buttonText: string;
    buttonAction: string;
    backgroundImage?: string;
    images?: string[];
  };
  colors: {
    background: string;
    primary: string;
    secondary: string;
    accent: string;
    text: string;
  };
}

interface StorefrontEditorProps {
  content: StorefrontContent;
  onChange: (content: Partial<StorefrontContent>) => void;
}

export function StorefrontEditor({ content, onChange }: StorefrontEditorProps) {
  const [activeTab, setActiveTab] = useState("colors");
  const [isUploading, setIsUploading] = useState(false);

  const handleColorChange = (
    colorKey: keyof StorefrontContent["colors"],
    value: string
  ) => {
    onChange({
      colors: {
        ...content.colors,
        [colorKey]: value,
      },
    });
  };

  const handleHeroChange = (
    heroKey: keyof StorefrontContent["hero"],
    value: string
  ) => {
    onChange({
      hero: {
        ...content.hero,
        [heroKey]: value,
      },
    });
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // Generate unique file name
        const fileExt = file.name.split(".").pop();
        const fileName = `hero-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2)}.${fileExt}`;
        const filePath = `hero-images/${fileName}`;

        // Upload to Supabase storage
        const { data, error } = await supabase.storage
          .from("landing-pages")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (error) {
          console.error("Upload error:", error);
          throw error;
        }

        // Get public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from("landing-pages").getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      // Update content with new images
      const updatedImages = [...(content.hero.images || []), ...uploadedUrls];
      onChange({
        hero: {
          ...content.hero,
          images: updatedImages,
          // If this is the first image, also set it as backgroundImage for backward compatibility
          backgroundImage: content.hero.backgroundImage || uploadedUrls[0],
        },
      });

      toast({
        title: "Images uploaded successfully",
        description: "Store images have been updated.",
      });
    } catch (error) {
      console.error("Error uploading images:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = [...(content.hero.images || [])];
    updatedImages.splice(index, 1);

    onChange({
      hero: {
        ...content.hero,
        images: updatedImages,
        // If removing the current background image, update it to the first remaining image
        backgroundImage:
          content.hero.backgroundImage === content.hero.images?.[index]
            ? updatedImages[0]
            : content.hero.backgroundImage,
      },
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">
          Storefront Editor
        </h2>
        <p className="text-sm text-gray-600">
          Customize your store's appearance
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="colors">
              <Palette className="h-4 w-4 mr-2" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="content">
              <Type className="h-4 w-4 mr-2" />
              Content
            </TabsTrigger>
            <TabsTrigger value="actions">
              <MousePointer className="h-4 w-4 mr-2" />
              Actions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="colors" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Color Scheme</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="background">Background Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="background"
                      type="color"
                      value={content.colors.background}
                      onChange={(e) =>
                        handleColorChange("background", e.target.value)
                      }
                      className="w-12 h-10 p-1 border-2"
                    />
                    <Input
                      type="text"
                      value={content.colors.background}
                      onChange={(e) =>
                        handleColorChange("background", e.target.value)
                      }
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="primary">Primary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="primary"
                      type="color"
                      value={content.colors.primary}
                      onChange={(e) =>
                        handleColorChange("primary", e.target.value)
                      }
                      className="w-12 h-10 p-1 border-2"
                    />
                    <Input
                      type="text"
                      value={content.colors.primary}
                      onChange={(e) =>
                        handleColorChange("primary", e.target.value)
                      }
                      placeholder="#6366F1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="secondary">Secondary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="secondary"
                      type="color"
                      value={content.colors.secondary}
                      onChange={(e) =>
                        handleColorChange("secondary", e.target.value)
                      }
                      className="w-12 h-10 p-1 border-2"
                    />
                    <Input
                      type="text"
                      value={content.colors.secondary}
                      onChange={(e) =>
                        handleColorChange("secondary", e.target.value)
                      }
                      placeholder="#EC4899"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="accent">Accent Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="accent"
                      type="color"
                      value={content.colors.accent}
                      onChange={(e) =>
                        handleColorChange("accent", e.target.value)
                      }
                      className="w-12 h-10 p-1 border-2"
                    />
                    <Input
                      type="text"
                      value={content.colors.accent}
                      onChange={(e) =>
                        handleColorChange("accent", e.target.value)
                      }
                      placeholder="#8B5CF6"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="text">Text Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="text"
                      type="color"
                      value={content.colors.text}
                      onChange={(e) =>
                        handleColorChange("text", e.target.value)
                      }
                      className="w-12 h-10 p-1 border-2"
                    />
                    <Input
                      type="text"
                      value={content.colors.text}
                      onChange={(e) =>
                        handleColorChange("text", e.target.value)
                      }
                      placeholder="#2C3E50"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="content" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Hero Section</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={content.hero.title}
                    onChange={(e) => handleHeroChange("title", e.target.value)}
                    placeholder="Welcome to Our Store"
                  />
                </div>

                <div>
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Textarea
                    id="subtitle"
                    value={content.hero.subtitle}
                    onChange={(e) =>
                      handleHeroChange("subtitle", e.target.value)
                    }
                    placeholder="Discover amazing products and services"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="buttonText">Button Text</Label>
                  <Input
                    id="buttonText"
                    value={content.hero.buttonText}
                    onChange={(e) =>
                      handleHeroChange("buttonText", e.target.value)
                    }
                    placeholder="Explore Now"
                  />
                </div>

                <div>
                  <Label htmlFor="images">Store Images</Label>
                  <div className="mt-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="imageUpload"
                      disabled={isUploading}
                      multiple
                    />
                    <Button
                      variant="outline"
                      onClick={() =>
                        document.getElementById("imageUpload")?.click()
                      }
                      className="w-full"
                      disabled={isUploading}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isUploading ? "Uploading..." : "Upload Store Images"}
                    </Button>

                    {(content.hero.images || []).length > 0 && (
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        {content.hero.images?.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Store image ${index + 1}`}
                              className="w-full h-32 object-cover rounded border"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white hover:bg-black/70"
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="actions" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Button Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="buttonAction">Hero Button Action</Label>
                  <Input
                    id="buttonAction"
                    value={content.hero.buttonAction}
                    onChange={(e) =>
                      handleHeroChange("buttonAction", e.target.value)
                    }
                    placeholder="#menu"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Use # for page sections (e.g., #menu) or full URLs
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
